import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { confirmDelete } from "@/lib/confirm";
import { useWs } from "@/context/WorkspaceContext";
import { MentionComposer, highlightMentions } from "@/components/MentionComposer";
import { StatusBadge, Avatar, fmtDate, fmtDateTime } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, Trash2, Plus, X, MessageCircle, GitCommit, MessagesSquare, Search } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "submitted", label: "Submitted" },
  { value: "in_progress", label: "In Progress" },
  { value: "on_hold", label: "On Hold" },
  { value: "awaiting_information", label: "Awaiting Information" },
  { value: "closed", label: "Closed" },
];

export default function TicketDetail() {
  const { slug } = useWs();
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [comment, setComment] = useState("");
  const [formData, setFormData] = useState(null);
  const [convOpen, setConvOpen] = useState(false);
  const [convQuery, setConvQuery] = useState("");
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [peopleQuery, setPeopleQuery] = useState("");

  const isAgency = ticket?.can_edit;

  const load = async () => {
    const { data } = await api.get(`/tickets/${ticketId}`);
    setTicket(data);
  };
  useEffect(() => {
    load();
    api.get("/tickets/form-data").then((r) => setFormData(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, [ticketId]);

  const update = async (payload) => {
    try {
      const { data } = await api.put(`/tickets/${ticketId}`, payload);
      setTicket(data);
      toast.success("Updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/tickets/${ticketId}/comment`, { message: comment });
      setComment("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    await api.delete(`/tickets/${ticketId}`);
    toast.success("Ticket deleted");
    navigate(`/${slug}/tickets`);
  };

  const attachConv = (cid) => {
    const current = (ticket.conversation_ids || []);
    if (current.includes(cid)) return;
    update({ conversation_ids: [...current, cid] });
    setConvOpen(false); setConvQuery("");
  };
  const detachConv = (cid) => {
    update({ conversation_ids: (ticket.conversation_ids || []).filter((x) => x !== cid) });
  };

  const addParticipant = async (uid) => {
    try {
      const { data } = await api.post(`/tickets/${ticketId}/participants`, { user_id: uid });
      setTicket(data);
      setPeopleOpen(false); setPeopleQuery("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeParticipant = async (uid) => {
    if (!(await confirmDelete({ message: "Remove this participant from the ticket?", confirmText: "Remove" }))) return;
    try {
      const { data } = await api.delete(`/tickets/${ticketId}/participants/${uid}`);
      setTicket(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!ticket) return <div className="text-[#9FA1A4] text-sm">Loading…</div>;

  const attachedIds = new Set(ticket.conversation_ids || []);
  const convResults = (formData?.conversations || [])
    .filter((c) => !attachedIds.has(c.id))
    .filter((c) => (c.subject || "").toLowerCase().includes(convQuery.toLowerCase()))
    .slice(0, 8);

  return (
    <div>
      <button data-testid="ticket-back-button" onClick={() => navigate(`/${slug}/tickets`)} className="flex items-center gap-1.5 text-sm text-[#9FA1A4] hover:text-[#06060C] mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> All tickets
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white p-6">
            <div className="flex items-start justify-between gap-4">
              <h1 className="font-display text-2xl font-semibold tracking-tight">{ticket.title}</h1>
              <StatusBadge status={ticket.status} testid="ticket-detail-status" />
            </div>
            {ticket.description && <p className="mt-3 text-sm text-[#595D62] leading-relaxed whitespace-pre-wrap">{ticket.description}</p>}
            <div className="mt-4 flex items-center gap-2 font-mono-data text-[10px] text-[#9FA1A4]">
              <span>Raised {fmtDate(ticket.created_at)} by {ticket.submitted_by_name || ticket.created_by_name}</span>
            </div>
          </div>

          {/* Activity */}
          <div className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white p-6">
            <h2 className="font-display text-lg font-medium mb-5">Activity timeline</h2>
            <div className="space-y-4">
              {ticket.updates?.map((u) => (
                <div key={u.id} data-testid="ticket-activity-item" className="flex gap-3">
                  <div className="mt-0.5 h-7 w-7 rounded-full bg-[#F6F6FD] border border-[#f0f0f3] flex items-center justify-center text-[#9FA1A4] shrink-0">
                    {u.type === "comment" ? <MessageCircle className="h-3.5 w-3.5" /> : u.type === "created" ? <Plus className="h-3.5 w-3.5" /> : <GitCommit className="h-3.5 w-3.5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-600 whitespace-pre-wrap break-words">{highlightMentions(u.message, u.mention_names)}</p>
                    <p className="font-mono-data text-[10px] text-[#c4c5cc] mt-0.5">{u.author_name} • {fmtDateTime(u.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <MentionComposer
                testidPrefix="ticket-comment"
                users={[...(formData?.agency_members || []), ...(formData?.client_users || [])]}
                placeholder="Add a comment / update… use @ to tag someone. Enter for a new line."
                onSend={async (message, mentions) => { await api.post(`/tickets/${ticketId}/comment`, { message, mentions }); load(); }}
              />
            </div>
          </div>
        </div>

        {/* Side */}
        <div className="space-y-6">
          <div className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white p-6">
            <h3 className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-4">Properties</h3>

            <div className="space-y-5">
              <div>
                <label className="text-xs text-[#9FA1A4] mb-1.5 block">Status</label>
                {isAgency ? (
                  <Select value={ticket.status} onValueChange={(v) => update({ status: v })}>
                    <SelectTrigger data-testid="ticket-status-select" className="bg-[#F6F6FD] border-[#EEEFEF]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-[#EEEFEF] text-[#06060C]">
                      {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : <StatusBadge status={ticket.status} />}
              </div>

              <div>
                <label className="text-xs text-[#9FA1A4] mb-1.5 block">Client owner</label>
                {isAgency ? (
                  <Select value={ticket.client_owner_id || "none"} onValueChange={(v) => update({ client_owner_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="ticket-client-owner-edit" className="bg-[#F6F6FD] border-[#EEEFEF]"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#EEEFEF] text-[#06060C]">
                      <SelectItem value="none">Unassigned</SelectItem>
                      {(formData?.client_users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <OwnerRow name={ticket.client_owner_name} account="client" userId={ticket.client_owner_id} hasAvatar={ticket.client_owner_has_avatar} />
                )}
              </div>

              <div>
                <label className="text-xs text-[#9FA1A4] mb-1.5 block">Agency owner</label>
                {isAgency ? (
                  <Select value={ticket.agency_owner_id || "none"} onValueChange={(v) => update({ agency_owner_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="ticket-agency-owner-edit" className="bg-[#F6F6FD] border-[#EEEFEF]"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#EEEFEF] text-[#06060C]">
                      <SelectItem value="none">Unassigned</SelectItem>
                      {(formData?.agency_members || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <OwnerRow name={ticket.agency_owner_name} account="agency" userId={ticket.agency_owner_id} hasAvatar={ticket.agency_owner_has_avatar} />
                )}
              </div>

              <div>
                <label className="text-xs text-[#9FA1A4] mb-1.5 block">Submitted by</label>
                <OwnerRow name={ticket.submitted_by_name || ticket.created_by_name} account={ticket.submitted_by_account} userId={ticket.submitted_by_id} hasAvatar={ticket.submitted_by_has_avatar} />
              </div>
            </div>

            {isAgency && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="ticket-delete-button" className="mt-6 flex items-center gap-1.5 text-xs text-rose-600 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /> Delete ticket</button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-white border-[#EEEFEF] text-[#06060C]">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete ticket?</AlertDialogTitle>
                    <AlertDialogDescription className="text-[#9FA1A4]">This cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-[#EEEFEF] text-[#06060C] hover:bg-[#F6F6FD]">Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid="ticket-delete-confirm" onClick={remove} className="bg-rose-500 hover:bg-rose-600 text-white">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>

          {/* Team / participants */}
          <div className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">Team on this ticket</h3>
              {isAgency && (() => {
                const memberIds = new Set((ticket.participants_detail || []).map((p) => p.id));
                const pool = [...(formData?.agency_members || []), ...(formData?.client_users || [])]
                  .filter((m) => !memberIds.has(m.id))
                  .filter((m) => (m.name || "").toLowerCase().includes(peopleQuery.toLowerCase()))
                  .slice(0, 8);
                return (
                  <Popover open={peopleOpen} onOpenChange={setPeopleOpen}>
                    <PopoverTrigger asChild>
                      <button data-testid="ticket-add-participant-button" className="flex items-center gap-1 text-xs text-[#723FFF] hover:text-[#5b2fd6]"><Plus className="h-3.5 w-3.5" /> Add</button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-72 bg-white border-[#EEEFEF] p-2">
                      <div className="relative mb-1.5">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9FA1A4]" />
                        <Input value={peopleQuery} onChange={(e) => setPeopleQuery(e.target.value)} className="bg-[#F6F6FD] border-[#EEEFEF] pl-8 h-8 text-sm" placeholder="Search teammates…" />
                      </div>
                      <div className="max-h-56 overflow-y-auto">
                        {pool.length === 0 ? <p className="text-xs text-[#9FA1A4] p-2 text-center">Everyone's already here.</p> : pool.map((m) => (
                          <button key={m.id} data-testid="add-participant-option" onClick={() => addParticipant(m.id)} className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-[#F6F6FD] text-left">
                            <Avatar name={m.name} size={24} account={m.account} userId={m.id} hasAvatar={m.has_avatar} />
                            <span className="text-sm text-[#06060C] truncate">{m.name}</span>
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                );
              })()}
            </div>
            {ticket.participants_detail?.length ? (
              <div className="space-y-2">
                {ticket.participants_detail.map((p) => (
                  <div key={p.id} data-testid="ticket-participant" className="group flex items-center gap-2.5">
                    <Avatar name={p.name} size={30} account={p.account} userId={p.id} hasAvatar={p.has_avatar} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-[#06060C] truncate leading-tight">{p.name}{p.id === ticket.submitted_by_id && <span className="ml-1.5 font-mono-data text-[9px] uppercase tracking-wider text-[#723FFF]">Submitter</span>}</p>
                      {p.job_title && <p className="text-[10px] text-[#9FA1A4] truncate">{p.job_title}</p>}
                    </div>
                    {isAgency && p.id !== ticket.submitted_by_id && (
                      <button data-testid="ticket-remove-participant" onClick={() => removeParticipant(p.id)} className="text-[#c4c5cc] hover:text-rose-600 opacity-0 group-hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[#c4c5cc]">No one added yet.</p>
            )}
            <p className="mt-3 text-[10px] text-[#9FA1A4]">Everyone here can see and comment on this ticket.</p>
          </div>

          {/* Linked conversations */}
          <div className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">Linked conversations</h3>
              {isAgency && (
                <Popover open={convOpen} onOpenChange={setConvOpen}>
                  <PopoverTrigger asChild>
                    <button data-testid="ticket-link-conversation-button" className="flex items-center gap-1 text-xs text-[#723FFF] hover:text-[#5b2fd6]"><Plus className="h-3.5 w-3.5" /> Attach</button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-72 bg-white border-[#EEEFEF] p-2">
                    <div className="relative mb-1.5">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9FA1A4]" />
                      <Input value={convQuery} onChange={(e) => setConvQuery(e.target.value)} className="bg-[#F6F6FD] border-[#EEEFEF] pl-8 h-8 text-sm" placeholder="Search conversations…" />
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                      {convResults.length === 0 ? <p className="text-xs text-[#9FA1A4] p-2 text-center">No conversations.</p> : convResults.map((c) => (
                        <button key={c.id} data-testid="attach-conversation-option" onClick={() => attachConv(c.id)} className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-[#F6F6FD] text-left">
                          <MessagesSquare className="h-3.5 w-3.5 text-[#9FA1A4] shrink-0" />
                          <span className="text-sm text-[#06060C] truncate">{c.subject}</span>
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            {ticket.linked_conversations?.length ? (
              <div className="space-y-2">
                {ticket.linked_conversations.map((c) => (
                  <div key={c.id} data-testid="ticket-linked-conversation" className="group flex items-center gap-2.5 rounded-lg bg-[#F6F6FD] border border-[#f0f0f3] px-3 py-2">
                    <MessagesSquare className="h-3.5 w-3.5 text-[#9FA1A4] shrink-0" />
                    <button onClick={() => navigate(`/${slug}/comms?c=${c.id}`)} className="text-xs text-[#595D62] hover:text-[#06060C] truncate flex-1 text-left">{c.subject}</button>
                    {isAgency && <button onClick={() => detachConv(c.id)} className="text-[#c4c5cc] hover:text-rose-600 opacity-0 group-hover:opacity-100"><X className="h-3 w-3" /></button>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[#c4c5cc]">No linked conversations.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function OwnerRow({ name, account, userId, hasAvatar }) {
  return (
    <div className="flex items-center gap-2">
      <Avatar name={name || "Unassigned"} size={26} account={account} userId={userId} hasAvatar={hasAvatar} />
      <span className="text-sm text-[#06060C]">{name || "Unassigned"}</span>
    </div>
  );
}
